// ReportsOverview.tsx — Today summary using getDailyStats for accurate counts
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { reportService } from '../../services/reportService';
import LoadingScreen from '../LoadingScreen';
import {
  TrendingUp, CheckCircle, XCircle, DollarSign, ArrowRight,
  Loader, RefreshCw, Clock, Activity
} from 'lucide-react';

const ReportsOverview: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, success: 0, failed: 0, pending: 0, amountUSD: 0, amountCDF: 0 });

  const today = new Date().toISOString().split('T')[0];

  const loadToday = useCallback(async () => {
    if (!user?.merchant_code) return;
    try {
      setLoading(true);

      // Fetch real stats + recent transactions in parallel
      const [dailyStats, recentPage] = await Promise.all([
        reportService.getDailyStats(user.merchant_code, 1).catch(() => null),
        reportService.searchTransactions({
          merchant_code: user.merchant_code,
          date_from: today, date_to: today,
          page: 1, limit: 15
        })
      ]);

      setTransactions(recentPage.transactions || []);

      if (dailyStats && dailyStats.daily_stats && dailyStats.daily_stats.length > 0) {
        // Use real aggregated stats from backend
        const todayStats = dailyStats.daily_stats[0];
        setStats({
          total: todayStats.total_transactions || 0,
          success: todayStats.successful_transactions || 0,
          failed: todayStats.failed_transactions || 0,
          pending: (todayStats.total_transactions || 0) - (todayStats.successful_transactions || 0) - (todayStats.failed_transactions || 0),
          amountUSD: 0,
          amountCDF: todayStats.total_amount || 0
        });
      } else {
        // Fallback: use total from search API
        setStats({
          total: recentPage.total || 0,
          success: 0, failed: 0, pending: 0,
          amountUSD: 0, amountCDF: 0
        });
      }
    } catch (error) {
      console.error('Error loading overview:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.merchant_code, today]);

  useEffect(() => { loadToday(); }, [loadToday]);

  const formatTime = (d: string) => new Date(d).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const formatAmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const statusBadge = (status: string) => {
    const s = status.toLowerCase();
    if (s.includes('success')) return 'bg-green-100 text-green-700';
    if (s.includes('fail')) return 'bg-red-100 text-red-700';
    return 'bg-yellow-100 text-yellow-700';
  };
  const methodBadge = (method: string) => {
    const m = method.toLowerCase();
    if (m.includes('orange')) return 'bg-orange-100 text-orange-700';
    if (m.includes('airtel')) return 'bg-red-100 text-red-700';
    if (m.includes('m-pesa') || m.includes('mpesa') || m.includes('vodacom')) return 'bg-green-100 text-green-700';
    if (m.includes('afrimoney')) return 'bg-purple-100 text-emerald-700';
    return 'bg-blue-100 text-blue-700';
  };

  if (loading) {
    return (
      <LoadingScreen fullscreen={false} message="Loading..." />
    );
  }

  return (
    <div className="space-y-4">
      {/* Quick action — TOP */}
      <button onClick={() => navigate('/reports/transactions')}
        className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 text-sm font-semibold shadow-lg shadow-emerald-500/20 transition-all">
        <TrendingUp className="h-4 w-4 mr-2" /> Search & Export Transactions
      </button>

      {/* Date + Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-emerald-500" />
          <span className="text-sm font-medium text-gray-700">
            {new Date().toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
          <span className="text-xs text-gray-400">Real-time</span>
        </div>
        <button onClick={loadToday} className="flex items-center px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 border border-gray-200">
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="h-4 w-4 text-emerald-500" />
            <span className="text-xs text-gray-500">Total</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-xs text-gray-500">Successful</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.success.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="h-4 w-4 text-red-500" />
            <span className="text-xs text-gray-500">Failed</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{stats.failed.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-emerald-500" />
            <span className="text-xs text-gray-500">Volume</span>
          </div>
          {stats.amountUSD > 0 && <p className="text-xl font-bold text-emerald-700">{formatAmt(stats.amountUSD)} USD</p>}
          {stats.amountCDF > 0 && <p className="text-xl font-bold text-emerald-700">{formatAmt(stats.amountCDF)} CDF</p>}
          {stats.amountUSD === 0 && stats.amountCDF === 0 && <p className="text-xl font-bold text-emerald-700">0.00</p>}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Recent Transactions</h3>
          <button onClick={() => navigate('/reports/transactions')}
            className="flex items-center text-xs text-emerald-600 hover:text-emerald-800 font-medium">
            View All <ArrowRight className="h-3 w-3 ml-1" />
          </button>
        </div>

        {transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Customer</th>
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Method</th>
                  <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {transactions.map((tx) => (
                  <tr key={tx.id || tx.paydrc_reference} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">{formatTime(tx.created_at)}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">
                      <div className="max-w-[140px] truncate">{tx.customer_details || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-2 text-xs font-semibold text-gray-900 whitespace-nowrap">
                      {formatAmt(parseFloat(tx.amount) || 0)} {tx.currency}
                    </td>
                    <td className="px-4 py-2">
                      <span className={'inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ' + methodBadge(tx.method)}>{tx.method}</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={'inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ' + statusBadge(tx.status)}>{tx.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10">
            <TrendingUp className="h-8 w-8 text-gray-300 mb-2" />
            <p className="text-xs text-gray-400">No transactions yet today</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsOverview;
